import { queryClient } from '@/query/queryClient';
import { sessionKeys } from '@/query/keys';
import { removeSessionData, writeSessionData } from '@/auth/session';
import { readPref, removePref, writePref } from '@/utils/prefs';

/**
 * The active role assignment of this tab (Authorization-Plan §6.2, §13.6).
 *
 * Each application has its own active assignment (owner 2026-11-04; server side:
 * identity service, user_active_assignments, one per user + application, and
 * this UI's token is for `partner`). The UI keeps it in page memory only, in the
 * TanStack Query cache (UI state plan phase 1): it is set when the server
 * confirmed it, at sign-in, at page load (RoleGate: GET me/assignments
 * `active_assignment_id` + `active_context`) or on a switch. Every request
 * sends it as `X-PNET-Expected-Assignment`; until it is confirmed no header is
 * sent (no check), and no context-specific UI is shown.
 *
 * Nothing about the assignment is written to browser storage any more. The
 * one exception is an id-only marker (`pnet.pref.role_state_for`): which
 * assignment the page context kept in localStorage (header brand / partner,
 * saved filters, ...) belongs to, so a role switched on another device is
 * noticed at page load and that state is reset.
 *
 * No React and no ApiProvider import here (ApiProvider imports this module).
 */

export type ContextType = 'INTERNAL' | 'BRAND' | 'RIGHT_HOLDER' | 'AGENCY';

/**
 * One entry of GET /identity/api/me/assignments (identity part 7l, owner
 * decision 2026-10-07: no access internals): the assignment id, a display
 * label ("<role> – <organisation>", the role name for Internal), the
 * application keys, is_active and expires_at. No role / context ids, no scope
 * summary, no privilege level.
 */
export interface AssignmentSummary {
  id: number;
  label: string;
  /** application keys (partner, dmc, arena) */
  applications?: string[];
  expires_at?: string | null;
  is_active?: boolean;
  /**
   * The admin level of this assignment (owner 2026-10-15): "super_admin" |
   * "admin" | "organisation_admin" | "privileged" | "read_only_privileged" |
   * "standard". Only "super_admin" is shown (a badge). Absent on older servers.
   */
  level?: string | null;
}

/**
 * The ACTIVE assignment's context, as GET me/assignments (`active_context`),
 * POST me/session/assignment and LoginData (`data.active_context`) answer it.
 */
export interface ActiveContext {
  role_assignment_id: number;
  type: string;
  id: number | null;
  brand_id: number | null;
  property_id: number | null;
}

/** GET /identity/api/me/assignments */
export interface AssignmentList {
  active_assignment_id: number | null;
  version?: string;
  assignments: AssignmentSummary[];
  /** the active assignment's context; null when none is active */
  active_context?: ActiveContext | null;
}

/** What the UI keeps about the active assignment. */
export interface ActiveAssignment {
  id: number;
  userId: number | null;
  contextType: ContextType;
  contextId: number | null;
  brandId: number | null;
  propertyId: number | null;
  /** the display label ("<role> – <organisation>") */
  label: string;
}

export const EXPECTED_ASSIGNMENT_HEADER = 'X-PNET-Expected-Assignment';

/** The context type as the numeric code the older components compare. */
export const LEGACY_USER_TYPE: Record<ContextType, number> = {
  INTERNAL: 1,
  BRAND: 2,
  RIGHT_HOLDER: 3,
  AGENCY: 4,
};

const CONTEXT_TYPES = Object.keys(LEGACY_USER_TYPE) as ContextType[];

/** Thrown by the sign-in flow when the user has no valid assignment (§6.2: 0 → "no access"). */
export class NoAccessError extends Error {
  constructor(message = 'no_access') {
    super(message);
    this.name = 'NoAccessError';
  }
}

export const toContextType = (value: unknown): ContextType | null => {
  const upper = String(value ?? '').toUpperCase();
  return (CONTEXT_TYPES as string[]).includes(upper) ? (upper as ContextType) : null;
};

/** INTERNAL→1, BRAND→2, RIGHT_HOLDER→3, AGENCY→4; null for anything else. */
export const legacyUserType = (contextType: unknown): number | null => {
  const type = toContextType(contextType);
  return type ? LEGACY_USER_TYPE[type] : null;
};

const positiveIntOrNull = (value: unknown): number | null => {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
};

/**
 * The assignment `entry` as the active one, with its context (`context` must
 * be that assignment's: GET me/assignments `active_context`, the POST
 * me/session/assignment answer or LoginData's `active_context`). null when
 * the context is missing, belongs to another assignment or has no known type.
 */
export const toActiveAssignment = (
  entry: Pick<AssignmentSummary, 'id' | 'label'>,
  context: unknown,
  userId: number | null = null,
): ActiveAssignment | null => {
  const id = positiveIntOrNull(entry?.id);
  const fromContext = assignmentFromLoginContext(context, userId);
  if (id === null || fromContext === null || fromContext.id !== id) return null;
  return { ...fromContext, label: String(entry.label ?? '') };
};

// ---- the assignment the stored page context belongs to (an id only) ----------------

/** prefs name: `pnet.pref.role_state_for` */
export const ROLE_STATE_OWNER_PREF = 'role_state_for';

const isAssignmentId = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0;

/** The assignment id the page context in localStorage was written under, or null. */
export const getRoleStateOwner = (): number | null =>
  readPref<number | null>(ROLE_STATE_OWNER_PREF, isAssignmentId, null);

const writeRoleStateOwner = (assignment: ActiveAssignment | null): void => {
  if (assignment) writePref(ROLE_STATE_OWNER_PREF, assignment.id);
  else removePref(ROLE_STATE_OWNER_PREF);
};

// ---- the active assignment (page memory, TanStack Query cache) ----------------------

/** The assignment this tab runs with, once the server confirmed it (null before). */
export const getTabAssignment = (): ActiveAssignment | null =>
  queryClient.getQueryData<ActiveAssignment | null>(sessionKeys.activeAssignment) ?? null;

/** Make `assignment` the active one for this tab (memory only). */
export const setActiveAssignment = (assignment: ActiveAssignment | null): void => {
  if (assignment) writeSessionData(sessionKeys.activeAssignment, assignment);
  else removeSessionData(sessionKeys.activeAssignment);
  writeRoleStateOwner(assignment);
};

/** Forget the active assignment (sign-out, no access). */
export const clearActiveAssignment = (): void => {
  removeSessionData(sessionKeys.activeAssignment);
  writeRoleStateOwner(null);
};

/** `{ 'X-PNET-Expected-Assignment': '<id>' }` once this tab's assignment is confirmed, else `{}`. */
export const expectedAssignmentHeaders = (): Record<string, string> => {
  const assignment = getTabAssignment();
  return assignment ? { [EXPECTED_ASSIGNMENT_HEADER]: String(assignment.id) } : {};
};

// ---- context-type shim -------------------------------------------------------------

/**
 * Role preview (Authorization-Plan §13.7): while the user previews a role,
 * the legacy `type` follows the PREVIEWED role's context (the tab assignment
 * stays the user's own, it is what X-PNET-Expected-Assignment names).
 */
let previewContextType: ContextType | null = null;

export const getPreviewContextType = (): ContextType | null => previewContextType;

const previewListeners = new Set<() => void>();

/** Called when the previewed context type changes (SessionProvider re-renders). */
export const subscribePreviewContextType = (listener: () => void): (() => void) => {
  previewListeners.add(listener);
  return () => {
    previewListeners.delete(listener);
  };
};

/** Set (or clear with null) the previewed context type; the user's `type` follows it. */
export const setPreviewContextType = (type: ContextType | null): void => {
  if (previewContextType === type) return;
  previewContextType = type;
  previewListeners.forEach((listener) => listener());
};

/** The active assignment (confirmed by the server for this tab), or null. */
export const getActiveAssignment = (): ActiveAssignment | null => getTabAssignment();

/**
 * The context type every UI decision about "what kind of user" is made on
 * (part 7, conversion rule 5): the previewed role's while previewing, else the
 * ACTIVE assignment's. null without an active assignment: then no
 * context-specific UI is shown (never `users.type`).
 */
export const getActiveContextType = (): ContextType | null =>
  previewContextType ?? getActiveAssignment()?.contextType ?? null;

/**
 * The user object with `type` = the active assignment's context type in the
 * legacy codes (INTERNAL→1, BRAND→2, RIGHT_HOLDER→3, AGENCY→4). The account's
 * own `users.type` is kept as `account_type` only (no decision reads it, part 7
 * rule 5). Without an active assignment (identity service unavailable, the
 * role not confirmed yet) `type` is null: no context-specific UI. Applied on
 * every read of the session user (ApiProvider.returnUserData, useSession), so
 * nothing stored has to be rewritten when the role or a preview changes.
 */
export function withActiveContextType<T extends Record<string, any> | null | undefined>(
  user: T,
  assignment: ActiveAssignment | null = getActiveAssignment(),
): T {
  if (!user || typeof user !== 'object') return user;
  const contextType = assignment ? (previewContextType ?? assignment.contextType) : null;
  return {
    ...user,
    account_type: 'account_type' in user ? user.account_type : (user.type ?? null),
    type: legacyUserType(contextType),
  } as T;
}

/**
 * An active context ({role_assignment_id, type, id, brand_id, property_id}:
 * LoginData's `data.active_context`, GET me/assignments `active_context`, the
 * POST me/session/assignment answer) as the active assignment, without a
 * label. null when absent or malformed.
 */
export const assignmentFromLoginContext = (
  value: unknown,
  userId: number | null = null,
): ActiveAssignment | null => {
  if (!value || typeof value !== 'object') return null;
  const ctx = value as Record<string, unknown>;
  const id = positiveIntOrNull(ctx.role_assignment_id);
  const contextType = toContextType(ctx.type);
  if (id === null || contextType === null) return null;
  return {
    id,
    userId,
    contextType,
    contextId: positiveIntOrNull(ctx.id),
    brandId: positiveIntOrNull(ctx.brand_id),
    propertyId: positiveIntOrNull(ctx.property_id),
    label: '',
  };
};

/** Test helper: forget the tab assignment and a previewed context type. */
export const resetTabAssignmentForTests = (): void => {
  removeSessionData(sessionKeys.activeAssignment);
  previewContextType = null;
};
